import axios, { AxiosError, AxiosInstance } from 'axios';
import { getApiBaseUrl } from '@/core/utils/apiUtils';
import type { HttpClientForQuery, HttpRequestConfig, HttpResponse } from '../../domain/interfaces/HttpClientForQuery';

let serverApiBaseUrlResolver: (() => string | undefined) | null = null;
let serverCookieHeaderResolver: (() => string | undefined) | null = null;

export function setServerApiBaseUrlResolver(
  resolver: (() => string | undefined) | null
): void {
  serverApiBaseUrlResolver = resolver;
}

export function setServerCookieHeaderResolver(
  resolver: (() => string | undefined) | null
): void {
  serverCookieHeaderResolver = resolver;
}

function getSsrApiBaseUrl(): string | undefined {
  return serverApiBaseUrlResolver?.();
}

function getSsrRequestCookieHeader(): string | undefined {
  const value = serverCookieHeaderResolver?.();
  if (!value || !value.trim()) return undefined;
  return value;
}

function resolveApiBaseUrl(): string {
  const serverBaseUrl = getSsrApiBaseUrl();
  if (serverBaseUrl?.trim()) return serverBaseUrl.trim().replace(/\/+$/, '');
  return getApiBaseUrl();
}

function hasCookieHeader(headers: Record<string, string>): boolean {
  return Object.keys(headers).some((key) => key.toLowerCase() === 'cookie');
}

/**
 * Axios HTTP Client Adapter
 * -------------------------
 * Infrastructure adapter using Axios
 * Implements HttpClientForQuery interface
 * This can be swapped with fetch, ky, or any other HTTP client adapter
 */

/**
 * Create axios instance for query clients
 * This is the core HTTP client configuration
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 30000, // 30 seconds
    withCredentials: false, // Don't send credentials to avoid CORS issues
  });

  // Setup request interceptor
  instance.interceptors.request.use(
    (config) => {
      // Log request URL in development for debugging
      if (import.meta.env.DEV) {
        const fullUrl = `${config.baseURL || ''}${config.url || ''}`;
        console.log('API Request:', config.method?.toUpperCase(), fullUrl);
      }
      
      // Add auth token if available
      // const token = localStorage.getItem('authToken');
      // if (token) {
      //   config.headers.Authorization = `Bearer ${token}`;
      // }
      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  // Setup response interceptor
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error: AxiosError) => {
      // Log error in development
      // Keep error as AxiosError - don't convert to ApiError here
      // The request() method will convert it to a plain serializable object
      if (import.meta.env.DEV) {
        console.error('API Error:', error.message, {
          status: error.response?.status,
          statusText: error.response?.statusText,
        });
      }
      
      // Reject with original AxiosError - request() method will handle conversion
      return Promise.reject(error);
    }
  );

  return instance;
};

/**
 * Axios instance for query clients
 * Shared instance with interceptors configured
 */
const axiosInstance = createAxiosInstance();

/**
 * Axios HTTP Client Adapter
 * Implements HttpClientForQuery using Axios
 */
export class AxiosHttpClientAdapter implements HttpClientForQuery {
  async request<T = unknown>(config: HttpRequestConfig): Promise<HttpResponse<T>> {
    try {
      const resolvedBaseUrl = resolveApiBaseUrl();
      const ssrCookieHeader = getSsrRequestCookieHeader();
      const requestHeaders: Record<string, string> = { ...(config.headers ?? {}) };
      // Node has no document.cookie; forward the incoming request Cookie header so
      // visitor-specific SSR fetches (recently-viewed) match the client session.
      if (ssrCookieHeader && !hasCookieHeader(requestHeaders)) {
        requestHeaders.cookie = ssrCookieHeader;
      }

      const result = await axiosInstance({
        baseURL: resolvedBaseUrl || undefined,
        url: config.url,
        method: config.method || 'GET',
        data: config.data,
        params: config.params,
        headers: requestHeaders,
        // Allow endpoints to opt into credentials (cookies) when required.
        withCredentials: config.withCredentials,
      });

      return {
        data: result.data as T,
        status: result.status,
        statusText: result.statusText,
        headers: result.headers as Record<string, string>,
      };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      throw {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data || err.message,
        message: err.message,
      };
    }
  }
}

/**
 * Create Axios HTTP client adapter instance
 * Factory function for creating HTTP client adapter
 */
export function createAxiosHttpClient(): HttpClientForQuery {
  return new AxiosHttpClientAdapter();
}

